import type { SelectedStep } from '~/server/logic/prisma/step-select';
import type { TestRunExecutionOutput } from '~/types';
import { useMemo, useState } from 'react';
import { Button, Divider, useComputedColorScheme, Text } from '@mantine/core';
import { VersionBadge } from '~/components/common/VersionBadge';
import ExecutionStepViewer from './ExecutionStepViewer';
import ScreenshotComparisonViewer from '~/components/common/ScreenshotComparisonViewer';
import { IconLayoutBottombarCollapseFilled, IconLayoutNavbarCollapseFilled } from '@tabler/icons-react';

export default function ExecutionViewer({
  outputSteps,
  testRunOutput,
  inputSteps,
  inputContentId,
  trunkContentId,
  testId,
}: {
  outputSteps: SelectedStep[];
  testRunOutput: TestRunExecutionOutput;
  inputSteps?: SelectedStep[];
  inputContentId?: string;
  trunkContentId?: string;
  testId?: string;
}) {
  const [screenshotViewer, setScreenshotViewer] = useState<{
    isOpen: boolean;
    originalOperationId?: string;
    newOperationId?: string;
    rightDirectScreenshotUrl?: string;
    rightDirectScreenshots?: { before?: string; action?: string; after?: string };
  }>({
    isOpen: false,
  });

  const [allExpanded, setAllExpanded] = useState<boolean | undefined>();
  const colorScheme = useComputedColorScheme();

  const selectOperationsForScreenshot = ({
    originalOperationId,
    newOperationId,
    rightDirectScreenshotUrl,
    rightDirectScreenshots,
  }: {
    originalOperationId?: string;
    newOperationId?: string;
    rightDirectScreenshotUrl?: string;
    rightDirectScreenshots?: { before?: string; action?: string; after?: string };
  }) => {
    // If no screenshot data is provided, close the viewer instead of opening with empty data
    const hasData =
      originalOperationId ||
      newOperationId ||
      rightDirectScreenshotUrl ||
      rightDirectScreenshots?.before ||
      rightDirectScreenshots?.action ||
      rightDirectScreenshots?.after;

    if (!hasData) {
      setScreenshotViewer({ isOpen: false });
      return;
    }

    setScreenshotViewer({
      isOpen: true,
      originalOperationId,
      newOperationId,
      rightDirectScreenshotUrl,
      rightDirectScreenshots,
    });
  };

  // Use inputSteps as the primary source to show the full original test, falling back to outputSteps.
  // Check length because parent may pass [] when inputTestContent is missing (older attempts).
  const stepsToIterate = inputSteps?.length ? inputSteps : outputSteps;

  // Key by input step ID to maintain consistent references to the original test structure,
  // even when steps aren't executed and don't have output step IDs.
  const {
    aiChangesByInputStepId,
    operationErrorsByStepId,
    conditionalBlockResultsByInputStepId,
    operationLogsByOperationId,
    operationErrorByOperationId,
  } = useMemo(() => {
    // Only include logs that have a defined operationId
    const logsWithOperationId = testRunOutput.logs.filter((log) => log.operationId !== undefined);

    return {
      // Group AI changes by input step ID (aiChange.stepId is the input step ID)
      aiChangesByInputStepId: Object.groupBy(testRunOutput.aiChanges, (aiChange) => aiChange.stepId),
      operationErrorsByStepId: Object.groupBy(testRunOutput.errors, (error) => error.stepId),
      // Group conditional block results by input step ID (result.stepId is the input step ID)
      conditionalBlockResultsByInputStepId: Object.groupBy(
        testRunOutput.conditionalBlockResults,
        (result) => result.stepId,
      ),
      // there should only be one error per operation
      operationErrorByOperationId: Object.fromEntries(
        Object.entries(Object.groupBy(testRunOutput.errors, (error) => error.operationId) ?? {}).map(
          ([operationId, errors]) => [operationId, errors?.at(0)],
        ),
      ),
      operationLogsByOperationId: Object.groupBy(logsWithOperationId, (log) => log.operationId!),
    };
  }, [testRunOutput]);

  return (
    <div className="flex h-full w-full flex-col gap-4">
      <ScreenshotComparisonViewer
        isOpen={screenshotViewer.isOpen}
        leftOperationId={screenshotViewer.originalOperationId}
        rightOperationId={screenshotViewer.newOperationId}
        rightDirectScreenshotUrl={screenshotViewer.rightDirectScreenshotUrl}
        rightDirectScreenshots={screenshotViewer.rightDirectScreenshots}
        onClose={() => setScreenshotViewer({ isOpen: false })}
      />
      <div className="mt-1 flex flex-col gap-4">
        {/* Header */}
        <div className="ml-8 flex items-center gap-4 pt-4">
          <div className="flex flex-1 gap-4">
            <div className="flex items-center gap-2">
              <Text className="font-semibold">Original Test</Text>
              <VersionBadge
                contentId={inputContentId || ''}
                trunkContentId={trunkContentId || ''}
                testId={testId}
              />
              <Divider
                orientation="vertical"
                className="my-1 ml-1"
              />
            </div>
            <Button
              variant="default"
              color={colorScheme === 'dark' ? 'gray.4' : 'gray.7'}
              size="compact-sm"
              onClick={() => {
                setAllExpanded((prev) => {
                  if (prev === undefined) {
                    return true;
                  }
                  return !prev;
                });
              }}
              className="mr-6"
              leftSection={
                allExpanded ?
                  <IconLayoutBottombarCollapseFilled size={16} />
                : <IconLayoutNavbarCollapseFilled size={16} />
              }
            >
              {allExpanded ? 'Collapse All' : 'Expand All'}
            </Button>
          </div>
          <div className="flex-1">
            <div className="ml-3 flex justify-between gap-2 text-sm">
              <div className="flex items-center gap-2">
                <Text className="font-semibold">Actual Test Run</Text>
              </div>
            </div>
          </div>
        </div>
      </div>
      {stepsToIterate.map((_step, index) => {
        const inputStep = inputSteps?.at(index);
        const outputStep = outputSteps.at(index);
        const inputStepId = inputStep?.id;
        const outputStepId = outputStep?.id;
        return (
          <ExecutionStepViewer
            key={inputStepId ?? outputStepId ?? `step-${index}`}
            outputStep={outputStep}
            inputStep={inputStep}
            stepIndex={index}
            aiChanges={inputStepId ? (aiChangesByInputStepId[inputStepId] ?? []) : []}
            inputOperationErrors={inputStepId ? (operationErrorsByStepId[inputStepId] ?? []) : []}
            outputOperationErrors={outputStepId ? (operationErrorsByStepId[outputStepId] ?? []) : []}
            conditionalBlockResults={inputStepId ? conditionalBlockResultsByInputStepId[inputStepId] : undefined}
            operationErrorByOperationId={operationErrorByOperationId}
            operationLogsByOperationId={operationLogsByOperationId}
            allExpanded={allExpanded}
            selectOperationsForScreenshot={selectOperationsForScreenshot}
            selectedOperations={screenshotViewer}
            testRunOutput={testRunOutput}
          />
        );
      })}
    </div>
  );
}
